
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ExternalLink } from "lucide-react";
import { SeoResult } from "../types";

interface KeywordsTabProps {
  result: SeoResult;
}

export const KeywordsTab = ({ result }: KeywordsTabProps) => {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Top Ranking Keywords</CardTitle>
          <CardDescription>Keywords your site currently ranks for in Google's top 100 results</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Keyword</th>
                  <th className="text-center py-3 px-4">Search Volume</th>
                  <th className="text-center py-3 px-4">Difficulty</th>
                  <th className="text-center py-3 px-4">Position</th>
                  <th className="text-left py-3 px-4">URL</th>
                </tr>
              </thead>
              <tbody>
                {result.keywords.map((keyword, index) => (
                  <tr key={index} className="border-b hover:bg-muted/50">
                    <td className="py-3 px-4 font-medium">{keyword.keyword}</td>
                    <td className="py-3 px-4 text-center">{keyword.volume.toLocaleString()}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        keyword.difficulty > 65 ? "bg-red-100 text-red-800" : 
                        keyword.difficulty > 50 ? "bg-yellow-100 text-yellow-800" : 
                        "bg-green-100 text-green-800"
                      }`}>
                        {keyword.difficulty}/100
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center font-medium">
                      <span className={keyword.position <= 10 ? "text-green-500" : keyword.position <= 20 ? "text-yellow-500" : "text-red-500"}>
                        #{keyword.position}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm truncate max-w-[200px]">
                      <a href={keyword.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline flex items-center">
                        {keyword.url.replace(/^https?:\/\//, '').split('/')[1] ? '.../' + keyword.url.split('/').slice(3).join('/') : '/'}
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Keyword Gap Analysis</CardTitle>
          <CardDescription>Keywords your competitors rank for but you don't</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {["local seo services", "affordable digital marketing", "website traffic analysis", "ecommerce seo strategy", "lead generation marketing"].map((keyword, index) => (
              <div key={index} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                <div className="flex-grow">
                  <div className="font-medium">{keyword}</div>
                  <div className="text-sm text-muted-foreground">
                    Volume: {(Math.floor(Math.random() * 10) + 1) * 1000}
                    <span className="mx-2">•</span>
                    Difficulty: {Math.floor(Math.random() * 30) + 40}/100
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm">Top Competitor Ranking:</div>
                  <div className="font-medium text-desert-sunset">
                    #{Math.floor(Math.random() * 9) + 1}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default KeywordsTab;
